import type { PmProject } from "../api/pm";
import type { ImputationRule, ShadowDay } from "../api/types";
import { partyLabel } from "./billing";
import { DAY_NAMES_FR, MONTH_NAMES_FR, plural } from "./format";
import { newRulePath } from "./nav";

/** Plage la plus longue que rend la lecture shadow : 31 jours, bornes incluses. */
export const MAX_SHADOW_SPAN_DAYS = 30;

export type ChantierPreset = "quatre-semaines" | "semaine" | "semaine-derniere";

export const CHANTIER_PRESETS: { key: ChantierPreset; label: string }[] = [
  { key: "quatre-semaines", label: "4 dernières semaines" },
  { key: "semaine", label: "Semaine en cours" },
  { key: "semaine-derniere", label: "Semaine dernière" },
];

export const TRUNK_LABEL = "Hors chantier (tronc)";
export const UNMAPPED_TAG = "Dépôt sans projet rattaché";
export const UNATTRIBUTED_LABEL = "Hors dépôt";
export const CLIENTS_LABEL = "Clients";
export const INTERNAL_LABEL = "Interne";
export const OPAQUE_LABEL = "Branche sans nom";

/** Sous ce seuil, une branche rejoint la ligne « Divers » de son projet. */
export const FOLD_SECONDS = 15 * 60;

const DAY_MS = 86_400_000;

function keyMs(key: string): number {
  const [y, m, d] = key.split("-").map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

function keyOf(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function addDays(key: string, days: number): string {
  return keyOf(keyMs(key) + days * DAY_MS);
}

/** Lundi de la semaine ISO d'un jour « YYYY-MM-DD ». */
export function mondayOf(key: string): string {
  const weekday = (new Date(keyMs(key)).getUTCDay() + 6) % 7;
  return addDays(key, -weekday);
}

/** Numéro de semaine ISO : celle qui contient le jeudi. */
export function isoWeek(key: string): number {
  const thursday = keyMs(addDays(mondayOf(key), 3));
  const jan1 = Date.UTC(new Date(thursday).getUTCFullYear(), 0, 1);
  return Math.floor((thursday - jan1) / DAY_MS / 7) + 1;
}

/** « S41 · 6 → 12 oct. », ou « S40 · 29 sept. → 5 oct. » à cheval sur deux mois. */
export function weekLabel(monday: string): string {
  const s = new Date(keyMs(monday));
  const e = new Date(keyMs(addDays(monday, 6)));
  const sm = MONTH_NAMES_FR[s.getUTCMonth()] ?? "";
  const em = MONTH_NAMES_FR[e.getUTCMonth()] ?? "";
  const start =
    s.getUTCMonth() === e.getUTCMonth()
      ? `${s.getUTCDate()}`
      : `${s.getUTCDate()} ${sm}`;
  return `S${isoWeek(monday)} · ${start} → ${e.getUTCDate()} ${em}`;
}

/** « 14 oct. ». */
export function dateLabel(key: string): string {
  const d = new Date(keyMs(key));
  return `${d.getUTCDate()} ${MONTH_NAMES_FR[d.getUTCMonth()] ?? ""}`;
}

/** « lun. 13 ». */
export function dayLabel(key: string): string {
  const d = new Date(keyMs(key));
  return `${DAY_NAMES_FR[d.getUTCDay()] ?? ""} ${d.getUTCDate()}`;
}

/** Période d'un préréglage, bornes incluses, jamais plus de 31 jours. */
export function chantierPeriod(
  preset: ChantierPreset,
  today: string,
): { from: string; to: string } {
  const monday = mondayOf(today);
  switch (preset) {
    case "semaine":
      return { from: monday, to: today };
    case "semaine-derniere":
      return { from: addDays(monday, -7), to: addDays(monday, -1) };
    case "quatre-semaines": {
      const from = addDays(monday, -21);
      const floor = addDays(today, -MAX_SHADOW_SPAN_DAYS);
      return { from: from > floor ? from : floor, to: today };
    }
  }
}

export interface ShadowRange {
  from: string;
  to: string;
}

/** Découpe [from, to] en lectures shadow de 31 jours au plus, de la plus
 * ancienne à la plus récente. */
export function shadowRanges(from: string, to: string): ShadowRange[] {
  const out: ShadowRange[] = [];
  for (
    let start = from;
    start <= to;
    start = addDays(start, MAX_SHADOW_SPAN_DAYS + 1)
  ) {
    const end = addDays(start, MAX_SHADOW_SPAN_DAYS);
    out.push({ from: start, to: end < to ? end : to });
  }
  return out;
}

/** Semaines du bilan d'un projet, semaine en cours comprise. */
export const BILAN_WEEKS = 12;

/** Au-delà, un chantier sans activité se signale. */
export const STALE_DAYS = 14;

/** Lundis des `count` dernières semaines ISO, semaine en cours comprise, du
 * plus ancien au plus récent. */
export function recentMondays(today: string, count = BILAN_WEEKS): string[] {
  const monday = mondayOf(today);
  return Array.from({ length: count }, (_, i) =>
    addDays(monday, -7 * (count - 1 - i)),
  );
}

/** Jours écoulés de `from` à `to`. */
export function daysBetween(from: string, to: string): number {
  return Math.round((keyMs(to) - keyMs(from)) / DAY_MS);
}

const TRUNK_BRANCHES = new Set([
  "main",
  "master",
  "develop",
  "developp",
  "staging",
]);
const AGENT_BRANCH = /^worktree-agent-/;
const CONVENTIONAL_TYPES = new Set([
  "feat",
  "fix",
  "chore",
  "refactor",
  "docs",
]);

function withoutUserPrefix(branch: string): string[] {
  const parts = branch.trim().split("/").filter(Boolean);
  return parts.length >= 2 && !CONVENTIONAL_TYPES.has(parts[0]!.toLowerCase())
    ? parts.slice(1)
    : parts;
}

/** Identité d'un chantier, commune à tous les dépôts : la branche sans préfixe
 * utilisateur, en minuscules. Le tronc, la branche vide et les worktrees
 * d'agent (`worktree-agent-*`) n'en ont pas : null. */
export function chantierKey(branch: string): string | null {
  const rest = withoutUserPrefix(branch).join("/").toLowerCase();
  if (!rest || TRUNK_BRANCHES.has(rest) || AGENT_BRANCH.test(rest)) return null;
  return rest;
}

/** Chemin d'un chantier, casse d'origine : la branche sans préfixe
 * utilisateur, que « henridevl/feat/x » et « thibaud/feat/x » partagent. null
 * sans identité de chantier (voir chantierKey). */
export function chantierPath(branch: string): string | null {
  return chantierKey(branch) === null
    ? null
    : withoutUserPrefix(branch).join("/");
}

/** Règle préremplie qui rattache une branche d'un dépôt : la branche exacte en
 * motif. Le tronc, la branche vide et les worktrees d'agent, sans identité de
 * chantier, rattachent le dépôt entier. */
export function branchRulePath(
  repo: string,
  branch: string,
  workerUrl: string,
): string {
  return chantierKey(branch) === null
    ? newRulePath(repo, workerUrl, "repo")
    : newRulePath(repo, workerUrl, "repo", branch);
}

/** Nom tiré au hasard par un workspace conductor (« 4kud8 », « aa339 ») :
 * cinq caractères dont un chiffre, rien de lisible. */
const OPAQUE_BRANCH = /^(?=.*\d)[a-z0-9]{5}$/;

export function isOpaqueChantier(key: string): boolean {
  return OPAQUE_BRANCH.test(key);
}

/** Libellé lisible d'une branche : « henridevl/fix/healthcheck-interval »
 * devient « Healthcheck interval ». Sans identité de chantier : hors chantier.
 * Un nom conductor aléatoire n'est pas un libellé. */
export function chantierLabel(branch: string): string {
  const key = chantierKey(branch);
  if (key === null) return TRUNK_LABEL;
  if (isOpaqueChantier(key)) return `${OPAQUE_LABEL} (${key})`;
  let parts = withoutUserPrefix(branch);
  if (parts.length >= 2 && CONVENTIONAL_TYPES.has(parts[0]!.toLowerCase())) {
    parts = parts.slice(1);
  }
  const words = parts
    .join(" / ")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function normalizeRepo(repo: string): string {
  return repo.trim().toLowerCase();
}

/** « senacle/ybe » devient « ybe ». */
export function shortRepo(repo: string): string {
  return repo.split("/").filter(Boolean).pop() ?? repo;
}

export interface RepoProject {
  projectId: string;
  name: string;
  /** Référence MDM du client du projet PM, null pour un projet interne. */
  clientUrl: string | null;
}

/** Projet PM d'une branche d'un dépôt, null sans règle. */
export type ProjectOf = (repo: string, branch: string) => RepoProject | null;

const NO_PROJECT: ProjectOf = () => null;

/** Motif glob du serveur (`MatchBranchPattern`) : « * » vaut toute suite de
 * caractères, « / » compris, « ? » un caractère, casse comprise. Une branche
 * vide ne correspond à aucun motif. */
export function matchBranchPattern(pattern: string, branch: string): boolean {
  if (!branch) return false;
  const p = [...pattern];
  const b = [...branch];
  let pi = 0;
  let bi = 0;
  let star = -1;
  let mark = 0;
  while (bi < b.length) {
    if (pi < p.length && (p[pi] === "?" || p[pi] === b[bi])) {
      pi++;
      bi++;
    } else if (pi < p.length && p[pi] === "*") {
      star = pi;
      mark = bi;
      pi++;
    } else if (star >= 0) {
      pi = star + 1;
      mark++;
      bi = mark;
    } else {
      return false;
    }
  }
  while (pi < p.length && p[pi] === "*") pi++;
  return pi === p.length;
}

function literalPrefixLen(pattern: string): number {
  const i = pattern.search(/[*?]/);
  return i >= 0 ? i : pattern.length;
}

/** Ordre du serveur (`ruleBefore`) restreint aux règles de dépôt et de
 * dossier : du salarié avant celle du tenant, à motif avant sans motif, dépôt
 * avant dossier, motif au plus long préfixe littéral, puis la plus ancienne. */
function ruleBefore(a: ImputationRule, b: ImputationRule): boolean {
  if (Boolean(a.worker_url) !== Boolean(b.worker_url)) {
    return Boolean(a.worker_url);
  }
  const pa = a.branch_pattern ?? "";
  const pb = b.branch_pattern ?? "";
  if (Boolean(pa) !== Boolean(pb)) return Boolean(pa);
  if (a.match_field !== b.match_field) return a.match_field === "repo";
  if (literalPrefixLen(pa) !== literalPrefixLen(pb)) {
    return literalPrefixLen(pa) > literalPrefixLen(pb);
  }
  if (a.created_at !== b.created_at) {
    return Date.parse(a.created_at) < Date.parse(b.created_at);
  }
  return a.id < b.id;
}

/** Projet PM d'une branche, comme le serveur choisit sa règle : active, sur le
 * dépôt ou sur le dossier de travail (sans casse), au motif de branche
 * satisfait s'il y en a un, la première dans l'ordre de `ruleBefore`. Les
 * observations ne portent pas le dossier : il est pris au dernier segment du
 * dépôt, ce qui manque un dossier renommé. Le client vient du projet PM, comme
 * pour la facturation ; un projet absent de PM est interne. */
export function resolveRepoProjects(
  rules: ImputationRule[],
  projects: PmProject[],
  selfUrl: string,
): ProjectOf {
  const byValue = new Map<string, ImputationRule[]>();
  for (const r of rules) {
    const value = normalizeRepo(r.match_value);
    if (
      !r.active ||
      (r.match_field !== "repo" && r.match_field !== "project") ||
      !value ||
      (r.worker_url && r.worker_url !== selfUrl)
    ) {
      continue;
    }
    const key = `${r.match_field}:${value}`;
    const list = byValue.get(key);
    if (list) list.push(r);
    else byValue.set(key, [r]);
  }
  for (const list of byValue.values()) {
    list.sort((a, b) => (ruleBefore(a, b) ? -1 : ruleBefore(b, a) ? 1 : 0));
  }
  const byId = new Map(projects.map((p) => [p.id, p]));
  return (repo, branch) => {
    const matches = (x: ImputationRule) =>
      !x.branch_pattern || matchBranchPattern(x.branch_pattern, branch.trim());
    const byRepo = byValue.get(`repo:${normalizeRepo(repo)}`)?.find(matches);
    const byFolder = byValue
      .get(`project:${normalizeRepo(shortRepo(repo))}`)
      ?.find(matches);
    const r =
      byRepo && byFolder
        ? ruleBefore(byFolder, byRepo)
          ? byFolder
          : byRepo
        : (byRepo ?? byFolder);
    if (!r) return null;
    const pm = byId.get(r.project_id);
    return {
      projectId: r.project_id,
      name: pm?.name || r.project_label || `Projet ${r.project_id.slice(0, 8)}`,
      clientUrl: pm?.client_party_url || null,
    };
  };
}

/** Heures arrondies au quart, en quarts d'heure. */
export function toQuarters(seconds: number): number {
  return Math.round(seconds / 900);
}

/** « 0,75 h », « 4,5 h », « 2 h ». */
export function formatQuarters(quarters: number): string {
  return `${String(quarters / 4).replace(".", ",")} h`;
}

/** Pull request GitHub d'un dépôt, vue le jour `date` (« YYYY-MM-DD »). */
export interface ChantierPr {
  repo: string;
  number: number;
  date: string;
}

export interface ChantierBranch {
  repo: string;
  branch: string;
  seconds: number;
  /** Pull request la plus récemment observée sur la branche. */
  pr: ChantierPr | null;
  /** Salarié qui a travaillé sur la branche ; vide hors vue équipe. */
  worker: string;
  workerName: string;
  /** Projet de la branche selon les règles de son salarié. */
  project: RepoProject | null;
  /** Secondes par jour « YYYY-MM-DD ». */
  days: Record<string, number>;
}

/** Temps d'un chantier un jour donné. */
export interface ChantierDay {
  date: string;
  seconds: number;
  quarters: number;
}

/** Temps d'un salarié sur un chantier. */
export interface ChantierPerson {
  workerUrl: string;
  name: string;
  seconds: number;
  quarters: number;
}

/** Premier et dernier jour travaillés sur un chantier, sur toute la période. */
export interface ChantierSpan {
  first: string;
  last: string;
}

export interface ChantierRepo {
  repo: string;
  seconds: number;
  quarters: number;
}

export interface ChantierLine {
  key: string;
  label: string;
  /** Dépôt affiché sous le libellé quand le projet en compte plusieurs et la
   * ligne un seul. */
  repo: string | null;
  /** Temps de la ligne par dépôt, le plus gros d'abord. */
  repos: ChantierRepo[];
  /** Branches brutes de la ligne. */
  branches: ChantierBranch[];
  /** Une pull request par dépôt, la plus récente ; aucune pour le tronc et
   * « Divers ». */
  prs: ChantierPr[];
  /** Temps par jour de la semaine, dans l'ordre du calendrier. */
  days: ChantierDay[];
  /** Temps par salarié, le plus gros d'abord. */
  people: ChantierPerson[];
  span: ChantierSpan | null;
  seconds: number;
  quarters: number;
}

export interface ChantierProject {
  key: string;
  name: string;
  mapped: boolean;
  clientUrl: string | null;
  repos: string[];
  seconds: number;
  /** Somme des lignes arrondies : la table copiée tombe juste. */
  quarters: number;
  lines: ChantierLine[];
}

export interface ChantierClient {
  key: string;
  label: string;
  seconds: number;
  quarters: number;
  projects: ChantierProject[];
}

export interface ChantierWeek {
  monday: string;
  label: string;
  seconds: number;
  unattributedSeconds: number;
  unattributedQuarters: number;
  clients: ChantierClient[];
  /** Projets sans client puis dépôts non rattachés. */
  internal: ChantierProject[];
  clientQuarters: number;
  /** Projets internes et hors dépôt, arrondis. */
  internalQuarters: number;
  /** Clients et interne, arrondis. */
  quarters: number;
}

/** URL GitHub d'une pull request. */
export function prUrl(pr: ChantierPr): string {
  return `https://github.com/${pr.repo}/pull/${pr.number}`;
}

/** « ybe 13,75 h · nesit-os-360 2 h » quand la ligne couvre plusieurs dépôts ;
 * un dépôt à 0 h n'est pas cité. */
export function repoSplitText(line: ChantierLine): string | null {
  const repos = line.repos.filter((r) => r.quarters > 0);
  if (repos.length < 2) return null;
  return repos
    .map((r) => `${shortRepo(r.repo)} ${formatQuarters(r.quarters)}`)
    .join(" · ");
}

/** « Travaillé le 14 oct. », « Travaillé du 13 au 21 oct. », « Travaillé du
 * 29 sept. au 2 oct. ». */
export function spanText(span: ChantierSpan): string {
  if (span.first === span.last) return `Travaillé le ${dateLabel(span.first)}`;
  const s = new Date(keyMs(span.first));
  const e = new Date(keyMs(span.last));
  const start =
    s.getUTCMonth() === e.getUTCMonth() &&
    s.getUTCFullYear() === e.getUTCFullYear()
      ? `${s.getUTCDate()}`
      : dateLabel(span.first);
  return `Travaillé du ${start} au ${dateLabel(span.last)}`;
}

/** « lun. 13 : 0,5 h · mar. 14 : 0,75 h » ; un jour à 0 h n'est pas cité. */
export function daySplitText(line: ChantierLine): string {
  return line.days
    .filter((d) => d.quarters > 0)
    .map((d) => `${dayLabel(d.date)} : ${formatQuarters(d.quarters)}`)
    .join(" · ");
}

/** Salariés de la ligne à plus de 0 h, le plus gros d'abord. */
export function linePeople(line: ChantierLine): ChantierPerson[] {
  return line.people.filter((p) => p.quarters > 0);
}

/** « Henri 2 h · Paul 1 h ». */
export function peopleText(line: ChantierLine, sep = " · "): string {
  return linePeople(line)
    .map((p) => `${p.name} ${formatQuarters(p.quarters)}`)
    .join(sep);
}

function bySecondsThenName<T extends { seconds: number }>(
  name: (x: T) => string,
): (a: T, b: T) => number {
  return (a, b) =>
    b.seconds - a.seconds || name(a).localeCompare(name(b), "fr");
}

function sumSeconds(list: { seconds: number }[]): number {
  return list.reduce((s, x) => s + x.seconds, 0);
}

function sumQuarters(list: { quarters: number }[]): number {
  return list.reduce((s, x) => s + x.quarters, 0);
}

function repoTotals(branches: ChantierBranch[]): ChantierRepo[] {
  const seconds = new Map<string, number>();
  for (const b of branches) {
    seconds.set(b.repo, (seconds.get(b.repo) ?? 0) + b.seconds);
  }
  return [...seconds.entries()]
    .map(([repo, s]) => ({ repo, seconds: s, quarters: 0 }))
    .sort(bySecondsThenName((r) => r.repo));
}

/** Répartit `total` quarts d'heure au plus fort reste : chaque part reçoit
 * l'arrondi inférieur de ses secondes, puis les quarts restants vont aux plus
 * grosses fractions. Les parts tombent juste sur le total. */
function apportion(seconds: number[], total: number): number[] {
  const out = seconds.map((s) => Math.floor(s / 900));
  let rest = total - out.reduce((a, b) => a + b, 0);
  const order = seconds
    .map((_, i) => i)
    .sort((a, b) => (seconds[b]! % 900) - (seconds[a]! % 900) || a - b);
  for (const i of order) {
    if (rest <= 0) break;
    out[i]!++;
    rest--;
  }
  return out;
}

function lineDays(branches: ChantierBranch[]): ChantierDay[] {
  const seconds = new Map<string, number>();
  for (const b of branches) {
    for (const [date, s] of Object.entries(b.days)) {
      seconds.set(date, (seconds.get(date) ?? 0) + s);
    }
  }
  return [...seconds.entries()]
    .filter(([, s]) => s > 0)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([date, s]) => ({ date, seconds: s, quarters: 0 }));
}

function linePeopleOf(branches: ChantierBranch[]): ChantierPerson[] {
  const people = new Map<string, ChantierPerson>();
  for (const b of branches) {
    const prev = people.get(b.worker);
    if (prev) prev.seconds += b.seconds;
    else
      people.set(b.worker, {
        workerUrl: b.worker,
        name: b.workerName,
        seconds: b.seconds,
        quarters: 0,
      });
  }
  return [...people.values()].sort(bySecondsThenName((p) => p.name));
}

function unionSpan(spans: (ChantierSpan | null)[]): ChantierSpan | null {
  let out: ChantierSpan | null = null;
  for (const s of spans) {
    if (!s) continue;
    if (!out) out = { ...s };
    else {
      if (s.first < out.first) out.first = s.first;
      if (s.last > out.last) out.last = s.last;
    }
  }
  return out;
}

function newerPr(a: ChantierPr, b: ChantierPr): boolean {
  return a.date !== b.date ? a.date > b.date : a.number > b.number;
}

/** La pull request la plus récente de chaque dépôt, dans l'ordre de `repos`. */
function linePrs(
  branches: ChantierBranch[],
  repos: ChantierRepo[],
): ChantierPr[] {
  const best = new Map<string, ChantierPr>();
  for (const b of branches) {
    if (!b.pr) continue;
    const prev = best.get(b.repo);
    if (!prev || newerPr(b.pr, prev)) best.set(b.repo, b.pr);
  }
  return repos.flatMap((r) => best.get(r.repo) ?? []);
}

type Chantier = {
  key: string;
  label: string;
  branches: ChantierBranch[];
  span: ChantierSpan | null;
};

function buildProject(
  key: string,
  name: string,
  mapped: boolean,
  clientUrl: string | null,
  chantiers: Chantier[],
): ChantierProject {
  const all = chantiers.flatMap((c) => c.branches);
  const repos = [...new Set(all.map((b) => b.repo))].sort((a, b) =>
    a.localeCompare(b, "fr"),
  );
  const several = repos.length > 1;
  const line = (c: Chantier): ChantierLine => {
    const seconds = sumSeconds(c.branches);
    const lineRepos = repoTotals(c.branches);
    return {
      key: c.key,
      label: c.label,
      repo: several && lineRepos.length === 1 ? lineRepos[0]!.repo : null,
      repos: lineRepos,
      branches: c.branches.sort(bySecondsThenName((b) => b.branch)),
      prs: c.key.startsWith("t:") ? [] : linePrs(c.branches, lineRepos),
      days: lineDays(c.branches),
      people: linePeopleOf(c.branches),
      span: c.span,
      seconds,
      quarters: 0,
    };
  };
  const lines = chantiers.map(line);
  const small = lines.filter((l) => l.seconds < FOLD_SECONDS);
  const kept =
    small.length >= 2 ? lines.filter((l) => l.seconds >= FOLD_SECONDS) : lines;
  kept.sort(bySecondsThenName((l) => l.label));
  if (small.length >= 2) {
    const seconds = sumSeconds(small);
    const branches = small
      .flatMap((l) => l.branches)
      .sort(bySecondsThenName((b) => b.branch));
    kept.push({
      key: "divers",
      label: `Divers (${small.length} chantier${plural(small.length)})`,
      repo: null,
      repos: repoTotals(branches),
      branches,
      prs: [],
      days: lineDays(branches),
      people: linePeopleOf(branches),
      span: unionSpan(small.map((l) => l.span)),
      seconds,
      quarters: 0,
    });
  }
  return {
    key,
    name,
    mapped,
    clientUrl,
    repos,
    seconds: sumSeconds(all),
    quarters: 0,
    lines: kept,
  };
}

type Owner = { key: string; name: string; project: RepoProject | null };

/** Projet d'un chantier : celui qui porte le plus de son temps, chaque branche
 * rattachée par les règles d'imputation de son salarié (motif de branche
 * compris), un dépôt sans règle comptant pour lui-même. À égalité, un projet
 * client d'abord. */
function ownerOf(branches: ChantierBranch[]): Owner {
  const owners = new Map<string, Owner & { seconds: number }>();
  for (const b of branches) {
    const project = b.project;
    const key = project ? `p:${project.projectId}` : `r:${b.repo}`;
    const prev = owners.get(key);
    if (prev) prev.seconds += b.seconds;
    else
      owners.set(key, {
        key,
        name: project ? project.name : b.repo,
        project,
        seconds: b.seconds,
      });
  }
  const [owner] = [...owners.values()].sort(
    (a, b) =>
      b.seconds - a.seconds ||
      Number(Boolean(b.project?.clientUrl)) -
        Number(Boolean(a.project?.clientUrl)) ||
      a.name.localeCompare(b.name, "fr"),
  );
  return owner!;
}

/** Libellé d'un chantier ; un nom conductor aléatoire prend celui de sa pull
 * request la plus récente quand il en a une. */
function labelOf(key: string, branches: ChantierBranch[]): string {
  if (key.startsWith("t:")) return TRUNK_LABEL;
  if (isOpaqueChantier(key.slice(2))) {
    const pr = branches.reduce<ChantierPr | null>(
      (best, b) => (b.pr && (!best || newerPr(b.pr, best)) ? b.pr : best),
      null,
    );
    if (pr) return `PR ${shortRepo(pr.repo)}#${pr.number}`;
  }
  return chantierLabel(branches[0]!.branch);
}

/** Observations d'un salarié, lues sous ses propres règles d'imputation. */
export interface ChantierSource {
  workerUrl: string;
  name: string;
  days: ShadowDay[];
  projectOf?: ProjectOf;
}

type SourceDay = { source: ChantierSource; day: ShadowDay };

type ShadowBranch = NonNullable<ShadowDay["repos"][number]["branches"]>[number];

/** Chaque branche d'un jour, avec sa clé de chantier ; un dépôt sans branches
 * vaut une branche vide. */
function eachBranch(
  d: ShadowDay,
  fn: (repo: string, b: ShadowBranch, key: string) => void,
) {
  for (const r of d.repos) {
    for (const b of r.branches ?? [
      { branch: "", server_seconds: r.server_seconds },
    ]) {
      const id = chantierKey(b.branch);
      fn(r.repo, b, id === null ? `t:${r.repo}` : `c:${id}`);
    }
  }
}

/** Premier et dernier jour à temps de chaque chantier, sur toute la période et
 * tous les salariés. */
function chantierSpans(list: SourceDay[]): Map<string, ChantierSpan> {
  const spans = new Map<string, ChantierSpan>();
  for (const { day: d } of list) {
    eachBranch(d, (_, b, key) => {
      if (b.server_seconds <= 0) return;
      const s = spans.get(key);
      if (!s) spans.set(key, { first: d.date, last: d.date });
      else {
        if (d.date < s.first) s.first = d.date;
        if (d.date > s.last) s.last = d.date;
      }
    });
  }
  return spans;
}

function weekChantiers(
  list: SourceDay[],
  spans: Map<string, ChantierSpan>,
): Chantier[] {
  const chantiers = new Map<string, Map<string, ChantierBranch>>();
  for (const { source, day: d } of list) {
    eachBranch(d, (repo, b, key) => {
      let branches = chantiers.get(key);
      if (!branches) {
        branches = new Map();
        chantiers.set(key, branches);
      }
      const bk = `${repo}\u0000${b.branch}\u0000${source.workerUrl}`;
      const pr: ChantierPr | null = b.pr_number
        ? { repo, number: b.pr_number, date: d.date }
        : null;
      const prev = branches.get(bk);
      if (prev) {
        prev.seconds += b.server_seconds;
        prev.days[d.date] = (prev.days[d.date] ?? 0) + b.server_seconds;
        if (pr && (!prev.pr || newerPr(pr, prev.pr))) prev.pr = pr;
      } else
        branches.set(bk, {
          repo,
          branch: b.branch,
          seconds: b.server_seconds,
          pr,
          worker: source.workerUrl,
          workerName: source.name,
          project: (source.projectOf ?? NO_PROJECT)(repo, b.branch),
          days: { [d.date]: b.server_seconds },
        });
    });
  }
  return [...chantiers.entries()].map(([key, m]) => {
    const branches = [...m.values()].sort(bySecondsThenName((b) => b.branch));
    return {
      key,
      label: labelOf(key, branches),
      branches,
      span: spans.get(key) ?? null,
    };
  });
}

function buildWeek(
  monday: string,
  list: SourceDay[],
  spans: Map<string, ChantierSpan>,
): ChantierWeek {
  const days = list.map((x) => x.day);
  type Group = Owner & { chantiers: Chantier[] };
  const groups = new Map<string, Group>();
  for (const c of weekChantiers(list, spans)) {
    const owner = ownerOf(c.branches);
    let g = groups.get(owner.key);
    if (!g) {
      g = { ...owner, chantiers: [] };
      groups.set(owner.key, g);
    }
    g.chantiers.push(c);
  }
  const built = [...groups.values()].map((g) =>
    buildProject(
      g.key,
      g.name,
      Boolean(g.project),
      g.project?.clientUrl ?? null,
      g.chantiers,
    ),
  );
  const unattributedSeconds = sumSeconds(
    days.map((d) => ({ seconds: d.unattributed_seconds })),
  );
  const leaves = built.flatMap((p) => p.lines);
  const shares = [...leaves.map((l) => l.seconds), unattributedSeconds];
  const quarters = apportion(
    shares,
    toQuarters(shares.reduce((a, b) => a + b, 0)),
  );
  leaves.forEach((l, i) => {
    l.quarters = quarters[i]!;
    const split = apportion(
      l.repos.map((r) => r.seconds),
      l.quarters,
    );
    l.repos.forEach((r, j) => (r.quarters = split[j]!));
    const daySplit = apportion(
      l.days.map((d) => d.seconds),
      l.quarters,
    );
    l.days.forEach((d, j) => (d.quarters = daySplit[j]!));
    const peopleSplit = apportion(
      l.people.map((p) => p.seconds),
      l.quarters,
    );
    l.people.forEach((p, j) => (p.quarters = peopleSplit[j]!));
  });
  const unattributedQuarters = quarters[leaves.length]!;
  for (const p of built) {
    p.lines = p.lines.filter((l) => l.quarters > 0);
    p.quarters = sumQuarters(p.lines);
  }
  const projects = built.filter((p) => p.quarters > 0);
  const byProject = bySecondsThenName<ChantierProject>((p) => p.name);
  const clients = new Map<string, ChantierClient>();
  for (const p of projects) {
    if (!p.clientUrl) continue;
    let c = clients.get(p.clientUrl);
    if (!c) {
      c = {
        key: p.clientUrl,
        label: partyLabel(p.clientUrl),
        seconds: 0,
        quarters: 0,
        projects: [],
      };
      clients.set(p.clientUrl, c);
    }
    c.seconds += p.seconds;
    c.quarters += p.quarters;
    c.projects.push(p);
  }
  for (const c of clients.values()) c.projects.sort(byProject);
  const internal = projects
    .filter((p) => !p.clientUrl)
    .sort((a, b) => Number(b.mapped) - Number(a.mapped) || byProject(a, b));
  const clientList = [...clients.values()].sort(
    bySecondsThenName((c) => c.label),
  );
  const clientQuarters = sumQuarters(clientList);
  const internalQuarters = sumQuarters(internal) + unattributedQuarters;
  return {
    monday,
    label: weekLabel(monday),
    seconds: sumSeconds(days.map((d) => ({ seconds: d.server_seconds }))),
    unattributedSeconds,
    unattributedQuarters,
    clients: clientList,
    internal,
    clientQuarters,
    internalQuarters,
    quarters: clientQuarters + internalQuarters,
  };
}

/** Regroupe les jours shadow par semaine ISO (la plus récente d'abord), puis par
 * chantier : une même branche fusionne entre dépôts et rejoint un seul projet,
 * celui qui porte le plus de son temps. Le tronc et les worktrees d'agent
 * restent par dépôt. Les projets à client forment la section « Clients », les
 * autres et les dépôts non rattachés la section « Interne ». Les chantiers de
 * moins de 15 minutes d'un projet se replient en une ligne « Divers ». Les
 * quarts d'heure se répartissent au plus fort reste, pour que les lignes
 * tombent juste sur le total de la semaine ; une ligne ou un projet à 0 h
 * n'est pas affiché. */
export function buildChantierWeeks(
  days: ShadowDay[],
  projectOf: ProjectOf = NO_PROJECT,
): ChantierWeek[] {
  return buildTeamChantierWeeks([{ workerUrl: "", name: "", days, projectOf }]);
}

/** Comme `buildChantierWeeks`, sur les observations de plusieurs salariés :
 * chaque branche est rattachée par les règles de son salarié, puis les
 * chantiers fusionnent entre salariés. Chaque ligne garde son temps par jour
 * et par salarié, et ses premier et dernier jours sur toute la période. */
export function buildTeamChantierWeeks(
  sources: ChantierSource[],
): ChantierWeek[] {
  const all = sources.flatMap((source) =>
    source.days.map((day) => ({ source, day })),
  );
  const spans = chantierSpans(all);
  const weeks = new Map<string, SourceDay[]>();
  for (const x of all) {
    const monday = mondayOf(x.day.date);
    const list = weeks.get(monday);
    if (list) list.push(x);
    else weeks.set(monday, [x]);
  }
  return [...weeks.entries()]
    .filter(([, list]) => list.some((x) => x.day.server_seconds > 0))
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([monday, list]) => buildWeek(monday, list, spans));
}

/** « ybe · feat/bilan », « ybe, os · feat/bilan » sur plusieurs dépôts,
 * « ybe · Hors chantier (tronc) » ; « Divers » garde son libellé. */
function chantierName(
  key: string,
  label: string,
  branches: ChantierBranch[],
): string {
  if (!branches.length || key === "divers") return label;
  const repos = repoTotals(branches)
    .map((r) => shortRepo(r.repo))
    .join(", ");
  const path = chantierPath(branches[0]!.branch);
  return `${repos} · ${path ?? TRUNK_LABEL}`;
}

/** Nom d'une ligne de semaine : dépôt et branche sans préfixe utilisateur. */
export function lineName(line: ChantierLine): string {
  return chantierName(line.key, line.label, line.branches);
}

/** Chantier d'un projet, cumulé sur toutes les semaines du bilan. */
export interface BilanChantier {
  key: string;
  name: string;
  label: string;
  people: ChantierPerson[];
  prs: ChantierPr[];
  /** Premier et dernier jour travaillés pour le projet. */
  span: ChantierSpan | null;
  /** Dernière activité il y a plus de STALE_DAYS jours. */
  stale: boolean;
  seconds: number;
  quarters: number;
}

export interface BilanWeek {
  monday: string;
  label: string;
  quarters: number;
  /** Lignes du projet cette semaine, comme dans l'onglet Chantiers. */
  lines: ChantierLine[];
}

export interface ProjectBilan {
  /** Une semaine par lundi demandé, vide comprise, la plus ancienne d'abord. */
  weeks: BilanWeek[];
  /** La plus récemment active d'abord. */
  chantiers: BilanChantier[];
  quarters: number;
}

function bilanChantiers(
  lines: ChantierLine[],
  quarters: number,
  today: string,
): BilanChantier[] {
  const groups = new Map<string, ChantierBranch[]>();
  for (const l of lines) {
    for (const b of l.branches) {
      const id = chantierKey(b.branch);
      const key = id === null ? `t:${b.repo}` : `c:${id}`;
      const list = groups.get(key);
      if (list) list.push(b);
      else groups.set(key, [b]);
    }
  }
  const out = [...groups.entries()].map(([key, branches]): BilanChantier => {
    branches.sort(bySecondsThenName((b) => b.branch));
    const days = lineDays(branches);
    const span = days.length
      ? { first: days[0]!.date, last: days[days.length - 1]!.date }
      : null;
    const label = labelOf(key, branches);
    return {
      key,
      name: chantierName(key, label, branches),
      label,
      people: linePeopleOf(branches),
      prs: key.startsWith("t:") ? [] : linePrs(branches, repoTotals(branches)),
      span,
      stale: span !== null && daysBetween(span.last, today) > STALE_DAYS,
      seconds: sumSeconds(branches),
      quarters: 0,
    };
  });
  const split = apportion(
    out.map((c) => c.seconds),
    quarters,
  );
  out.forEach((c, i) => {
    c.quarters = split[i]!;
    const people = apportion(
      c.people.map((p) => p.seconds),
      c.quarters,
    );
    c.people.forEach((p, j) => (p.quarters = people[j]!));
  });
  return out
    .filter((c) => c.quarters > 0)
    .sort(
      (a, b) =>
        (b.span?.last ?? "").localeCompare(a.span?.last ?? "") ||
        bySecondsThenName<BilanChantier>((c) => c.name)(a, b),
    );
}

/** Bilan d'un projet sur les semaines de `mondays` : ses lignes de chaque
 * semaine telles que l'onglet Chantiers les rattache, puis chaque chantier
 * cumulé, ses quarts répartis au plus fort reste sur le total des semaines. */
export function buildProjectBilan(
  weeks: ChantierWeek[],
  projectId: string,
  mondays: string[],
  today: string,
): ProjectBilan {
  const key = `p:${projectId}`;
  const byMonday = new Map(
    weeks.map((w) => [
      w.monday,
      [...w.clients.flatMap((c) => c.projects), ...w.internal].find(
        (p) => p.key === key,
      ),
    ]),
  );
  const bilanWeeks = mondays.map((monday): BilanWeek => {
    const p = byMonday.get(monday);
    return {
      monday,
      label: weekLabel(monday),
      quarters: p?.quarters ?? 0,
      lines: p?.lines ?? [],
    };
  });
  const quarters = sumQuarters(bilanWeeks);
  return {
    weeks: bilanWeeks,
    chantiers: bilanChantiers(
      bilanWeeks.flatMap((w) => w.lines),
      quarters,
      today,
    ),
    quarters,
  };
}

/** Série du graphique : un chantier, ou « Autres chantiers ». */
export interface BilanSeries {
  key: string;
  name: string;
}

export const OTHERS_KEY = "autres";

/** Les `max` chantiers les plus lourds, puis « Autres chantiers » s'il en
 * reste. */
export function bilanSeries(bilan: ProjectBilan, max = 5): BilanSeries[] {
  const top = [...bilan.chantiers]
    .sort(bySecondsThenName((c) => c.name))
    .slice(0, max)
    .map((c) => ({ key: c.key, name: c.name }));
  const rest = bilan.weeks.some((w) =>
    w.lines.some((l) => !top.some((s) => s.key === l.key)),
  );
  return rest ? [...top, { key: OTHERS_KEY, name: "Autres chantiers" }] : top;
}

/** Quarts d'une semaine par série, dans l'ordre des séries. */
export function weekSegments(
  week: BilanWeek,
  series: BilanSeries[],
): { series: BilanSeries; quarters: number }[] {
  const quarters = new Map<string, number>();
  for (const l of week.lines) {
    const key = series.some((s) => s.key === l.key) ? l.key : OTHERS_KEY;
    quarters.set(key, (quarters.get(key) ?? 0) + l.quarters);
  }
  return series
    .map((s) => ({ series: s, quarters: quarters.get(s.key) ?? 0 }))
    .filter((s) => s.quarters > 0);
}

/** Texte brut d'une semaine pour un compte rendu client : une ligne par
 * chantier client, « Client · Projet · Chantier · 0,75 h », suivi de ses pull
 * requests en référence complète (« owner/repo#N ») pour que le rapport hebdo
 * les relise sur GitHub, puis l'interne (hors dépôt compris) en une ligne. Un
 * chantier à plusieurs salariés porte leur part après les heures, entre
 * parenthèses. */
export function weekCopyText(week: ChantierWeek): string {
  const out = [`${week.label} · ${formatQuarters(week.quarters)}`];
  for (const c of week.clients) {
    for (const p of c.projects) {
      for (const l of p.lines) {
        const pr = l.prs.length
          ? ` (${l.prs.map((x) => `${x.repo}#${x.number}`).join(", ")})`
          : "";
        const label = l.repo
          ? `${l.label}${pr} (${shortRepo(l.repo)})`
          : `${l.label}${pr}`;
        const who = linePeople(l).length > 1 ? ` (${peopleText(l, ", ")})` : "";
        out.push(
          `${c.label} · ${p.name} · ${label} · ${formatQuarters(l.quarters)}${who}`,
        );
      }
    }
  }
  if (week.internalQuarters > 0) {
    out.push(`${INTERNAL_LABEL} · ${formatQuarters(week.internalQuarters)}`);
  }
  return out.join("\n");
}
